/**
 * KEEP IN SYNC with src/lib/cancel-chat-admin/motivation-chips.ts (admin mirror
 * used to reconstruct chips for older transcripts).
 *
 * Tappable answers to the chat's OPENING question ("what drew you to X to begin
 * with?").
 *
 * These are deliberately deterministic and hardcoded, not AI-authored: the
 * greeting is rendered instantly with no LLM round-trip, and the whole point of
 * that is the customer never waits on a spinner before the first word. Asking a
 * model for chips would cost exactly the round-trip the greeting exists to avoid.
 *
 * Three rules govern the copy, and each one is a trust rule, not a style rule:
 *
 *  1. GOALS, NOT SOURCES. "A friend recommended it" is a true answer and a
 *     useless one — the save uses this signal to offer a DIFFERENT product that
 *     serves the SAME goal, and a referral gives it nothing to work with. Every
 *     chip names something the catalogue can actually answer.
 *
 *  2. FORMAT-CORRECT. A "For my skin" chip under Pure Rose Nectar — a DRINK —
 *     is the same class of mistake PRODUCT_CATALOG (see src/lib/cancel-chat/
 *     prompt.ts) exists to prevent, except worse, because it lands in the very
 *     first message. Ingested and applied products get different chips, and a
 *     subscription that mixes both gets neither.
 *
 *  3. NO CLAIMS THE PRODUCT DOESN'T MAKE. Free text lets the CUSTOMER say they
 *     bought it to sleep better. A chip saying "For better sleep" is the STORE
 *     saying it. The Nectar is sold for calm and clarity and makes no sleep
 *     claim, so no chip may imply one. Keep every chip inside the product's
 *     existing marketing claims.
 *
 * Single-tenant today, mirroring PRODUCT_CATALOG on the server. If the chatbot
 * goes multi-shop, source these per shop (the cancel-chat-config endpoint is
 * already fetched before the sheet opens and would carry them at no latency
 * cost) rather than growing this table.
 */

/**
 * How the customer takes the product — it decides which goals are even coherent.
 * The Nectar is the only one you DRINK, which is why it alone can be hydrating.
 */
type Format = "nectar" | "yummies" | "mist" | "oil";

/**
 * The store's own benefit language. Deliberately NOT padded to three: a filler
 * chip ("A tasty treat") is worse than a shorter list — it wastes the one row
 * of screen we get, and it tells the customer nothing they'd actually claim as
 * their reason. Two real motivations beat three with a passenger.
 */
const CHIPS: Record<Format, string[]> = {
  // Drops stirred into water, tea or coffee — so it, and only it, hydrates.
  nectar: ["Stress relief", "Better mood", "Delicious hydration"],
  // Chewable gummies for calm. "Hydration" is nonsense for a gummy, and there
  // is no honest third benefit in the store's vocabulary — so it gets two.
  yummies: ["Stress relief", "Better mood"],
  // A face/body spray that hydrates and refreshes.
  mist: ["Glowing skin", "Hydrated skin"],
  // A facial oil that nourishes and softens fine lines, scars and dryness.
  oil: ["Glowing skin", "Softer skin", "Less dryness"],
};

/**
 * Used when the subscription mixes formats (something you take AND something
 * you apply), or when a title isn't recognised. Every entry is true somewhere
 * in the range — an unknown product must degrade, never guess.
 */
const GENERIC = ["Stress relief", "Better mood", "Glowing skin"];

/** Whether you take the product or put it on your skin. */
type Group = "taken" | "applied";

const GROUP: Record<Format, Group> = {
  nectar: "taken",
  yummies: "taken",
  mist: "applied",
  oil: "applied",
};

/**
 * Chips for a subscription with SEVERAL products of the same group. They must
 * be true of every line, so they're the benefits the group shares — a chip that
 * only holds for one of two products would be a claim about the other.
 */
const GROUP_CHIPS: Record<Group, string[]> = {
  taken: ["Stress relief", "Better mood"],
  applied: ["Glowing skin", "Hydrated skin"],
};

/**
 * Classify a product title. Matched on the distinctive token rather than the
 * whole string, because storefront titles carry sizes and variants
 * ("Pure Rose Nectar - 30ml"). Every title in the range contains "rose", so the
 * distinguishing token is what's tested.
 */
function classify(title: string): Format | null {
  const t = title.trim().toLowerCase();
  if (!t) return null;
  if (t.includes("nectar")) return "nectar";
  if (t.includes("yummies") || t.includes("gummies")) return "yummies";
  if (t.includes("mist")) return "mist";
  if (t.includes("oil") || t.includes("rosehip")) return "oil";
  return null;
}

/**
 * Two or three tappable motivations for the opening question. `siblingLineTitles`
 * are the OTHER products on the same subscription — once there are several, the
 * greeting speaks about the subscription as a whole, so the chips have to be
 * true of all of it.
 */
export function getMotivationChips(
  productTitle: string,
  siblingLineTitles: readonly string[] = [],
): string[] {
  const titles = [productTitle, ...siblingLineTitles];
  const formats = titles.map(classify);

  // One unrecognised line and we can't vouch for any of the chips, so don't.
  if (formats.some((f) => f == null)) return GENERIC;
  const known = formats as Format[];
  if (known.length === 0) return GENERIC;

  // A single product gets its own, richest set.
  if (known.length === 1) return CHIPS[known[0]];

  // Several products: only what is true of all of them. A subscription that
  // mixes something you drink with something you rub on your face has no shared
  // benefit worth naming, so it falls back.
  const groups = known.map((f) => GROUP[f]);
  const [first, ...rest] = groups;
  return rest.every((g) => g === first) ? GROUP_CHIPS[first] : GENERIC;
}
